/**
 * Нормализация распознанного текста для однострочных полей (поиск, названия):
 * переводы строк → пробел, одиночная точка в конце убирается (Whisper всегда завершает
 * фразу точкой, а в названии участка/поисковом запросе она лишняя). Многоточие и «?»/«!»
 * сохраняются — они могут быть частью названия.
 */
export function normalizeSingleLine(text: string): string {
  return text.replace(/[\r\n]+/g, ' ').trim().replace(/(?<!\.)\.$/, '').trimEnd()
}

export interface InsertResult {
  /** Новое значение поля */
  next: string
  /** Позиция каретки после вставленного текста */
  caret: number
}

const NO_SPACE_AFTER = /[\s(\[{«"'\u2014\u2013-]$/
const NO_SPACE_BEFORE = /^[\s.,!?;:)\]}»"']/

/**
 * Вставляет `text` в `value` на место выделения `[selStart, selEnd)`, добавляя пробел
 * на стыках, если слева/справа уже есть текст без пробела/пунктуации.
 * Если позиция не задана (поле ни разу не было в фокусе) — вставка в конец.
 */
export function insertAtCursor(
  value: string,
  text: string,
  selStart?: number | null,
  selEnd?: number | null,
): InsertResult {
  const insert = text.trim()
  if (!insert) {
    const caret = selEnd ?? value.length
    return { next: value, caret: Math.min(caret, value.length) }
  }

  const len = value.length
  let start = selStart ?? len
  let end = selEnd ?? start
  start = Math.max(0, Math.min(start, len))
  end = Math.max(start, Math.min(end, len))

  const before = value.slice(0, start)
  const after = value.slice(end)

  const needsSpaceBefore = before.length > 0 && !NO_SPACE_AFTER.test(before)
  const needsSpaceAfter = after.length > 0 && !NO_SPACE_BEFORE.test(after)

  const chunk = `${needsSpaceBefore ? ' ' : ''}${insert}${needsSpaceAfter ? ' ' : ''}`
  const next = before + chunk + after
  const caret = before.length + chunk.length

  return { next, caret }
}
